import { NuggetVariant } from '@backrs_ui/common-ui';

import { NUGGET_VARIANTS } from '@backrs_ui/common-ui/src/models/nugget';
import {
  searchForContentNuggets,
  resetSearchParamsForCategory,
  setSearchLoading,
} from '@backrs_ui/grow-ui/src/redux/search/search';
import { AppDispatch } from '@backrs_ui/grow-ui/src/redux/store';

import {
  ALL_SEARCH_CATEGORIES,
  SEARCH_CATEGORIES_INTERNSHIPS,
  SEARCH_CATEGORIES_SCHOLARSHIPS,
} from './search.filters';
import { SEARCH_PAGE_CATEGORIES, SearchCategory, SearchPageCategory } from './search.types';

export const fetchNuggetsUserHasSearched = async (
  dispatch: AppDispatch,
  search_term: string,
  page: number,
  types?: SearchCategory[],
  category: SearchPageCategory = SEARCH_PAGE_CATEGORIES.DEFAULT as SearchPageCategory,
) => {
  try {
    await dispatch(searchForContentNuggets({ search_term, page, types, category }));
  } catch (error) {
    console.error('Failed to fetch content nuggets:', error);
  }
};

type HandleInitiateSearchProps = Readonly<{
  dispatch: AppDispatch;
  currentSearchTerm: string;
  pageNum: number;
  items: SearchCategory[];
  searchPageCategory: SearchPageCategory;
  hasInitialSearchBeenMade?: boolean;
}>;

export const handleInitiateSearch = ({
  dispatch,
  currentSearchTerm,
  pageNum,
  items,
  searchPageCategory,
  hasInitialSearchBeenMade, // we assume the initial search has been made
}: HandleInitiateSearchProps) => {
  // Only initiate search if there's a search term OR if there are selected filter items
  if (
    (!currentSearchTerm || currentSearchTerm === '') &&
    (!items || items.length === 0) &&
    hasInitialSearchBeenMade
  ) {
    return;
  }

  dispatch(setSearchLoading(true));

  if (searchPageCategory === SEARCH_PAGE_CATEGORIES.SCHOLARSHIPS) {
    const scholarshipPageSelectedItems = items?.length
      ? items
      : [
          !hasInitialSearchBeenMade
            ? SEARCH_CATEGORIES_SCHOLARSHIPS.PRE_FILTERED
            : ALL_SEARCH_CATEGORIES.SCHOLARSHIPS_SCHOLARSHIPS,
        ];

    fetchNuggetsUserHasSearched(
      dispatch,
      currentSearchTerm || '',
      pageNum,
      scholarshipPageSelectedItems,
      searchPageCategory,
    );

    return;
  }

  if (searchPageCategory === SEARCH_PAGE_CATEGORIES.INTERNSHIPS) {
    const nonItemSearch = !hasInitialSearchBeenMade
      ? [SEARCH_CATEGORIES_INTERNSHIPS.JOB_TYPE_ALL]
      : [
          ALL_SEARCH_CATEGORIES.CAREER_OPPORTUNITIES_INTERNSHIPS,
          ALL_SEARCH_CATEGORIES.CAREER_OPPORTUNITIES_JOBS,
        ];

    const internshipPageSelectedItems = items?.length ? items : nonItemSearch;

    fetchNuggetsUserHasSearched(
      dispatch,
      currentSearchTerm || '',
      pageNum,
      internshipPageSelectedItems,
      searchPageCategory,
    );

    return;
  }

  if (searchPageCategory === SEARCH_PAGE_CATEGORIES.COLLEGES) {
    const universityPageSelectedItems = items?.length ? items : [ALL_SEARCH_CATEGORIES.COLLEGE_ALL];

    fetchNuggetsUserHasSearched(
      dispatch,
      currentSearchTerm || '',
      pageNum,
      universityPageSelectedItems,
      searchPageCategory,
    );

    return;
  }

  if (searchPageCategory === SEARCH_PAGE_CATEGORIES.EMPLOYERS) {
    const employerPageSelectedItems = items?.length ? items : [ALL_SEARCH_CATEGORIES.EMPLOYER_ALL];

    fetchNuggetsUserHasSearched(
      dispatch,
      currentSearchTerm || '',
      pageNum,
      employerPageSelectedItems,
      searchPageCategory,
    );

    return;
  }

  fetchNuggetsUserHasSearched(
    dispatch,
    currentSearchTerm || '',
    pageNum,
    items,
    searchPageCategory,
  );
};

/**
 * Clears the page's search term and filters, then re-runs the search that page
 * lands on. Without the re-run the page would be left showing nothing.
 */
export const resetSearchToLandingState = (
  dispatch: AppDispatch,
  searchPageCategory: SearchPageCategory,
) => {
  dispatch(resetSearchParamsForCategory(searchPageCategory));

  // The default page has no landing results, so a cleared page is its initial state.
  if (searchPageCategory === SEARCH_PAGE_CATEGORIES.DEFAULT) {
    return;
  }

  handleInitiateSearch({
    dispatch,
    currentSearchTerm: '',
    pageNum: 1,
    items: [],
    searchPageCategory,
    hasInitialSearchBeenMade: false,
  });
};

export const formatNuggetVariantName = (variant: NuggetVariant) => {
  switch (variant) {
    case NUGGET_VARIANTS.CAREER_PATH:
      return 'Career Path';
    case NUGGET_VARIANTS.CAREER_TRACK:
      return 'Career Track';
    case NUGGET_VARIANTS.EDUCATIONAL_INVESTMENT:
      return 'Educational Investment';
    case NUGGET_VARIANTS.PROFILE_EXTERNAL:
    case NUGGET_VARIANTS.PROFILE_INTERNAL:
      return 'Profile';
    case NUGGET_VARIANTS.GENERATED_CONTENT:
      return 'Generated Content';
    case NUGGET_VARIANTS.TEXT_RESPONSE:
      return 'Text Response';
    case NUGGET_VARIANTS.TRACK_ASSESSMENT_RESULT:
      return 'Track Assessment Result';
    case NUGGET_VARIANTS.TRACK_ASSESSMENT_UNLOCK:
      return 'Track Assessment Unlock';
    case NUGGET_VARIANTS.VIDEO_EXTERNAL:
    case NUGGET_VARIANTS.VIDEO_INTERNAL:
      return 'Video';
    case NUGGET_VARIANTS.MAJOR:
      return 'Major';
    case NUGGET_VARIANTS.INDUSTRY:
      return 'Industry';

    default:
      return variant.charAt(0).toUpperCase() + variant.slice(1);
  }
};
